import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Mail, Send, Eye, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const AdminCampaigns = () => {
  const { authHeaders } = useOutletContext();
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", body: "" });
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [openCampaign, setOpenCampaign] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/campaigns`, { headers: authHeaders }),
      axios.get(`${API}/admin/newsletter`, { headers: authHeaders }),
    ])
      .then(([c, s]) => {
        setCampaigns(c.data);
        setSubscribers(s.data.length);
      })
      .catch(() => toast.error("Laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authHeaders]);

  const send = async (testOnly = false) => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Onderwerp en bericht zijn verplicht");
      return;
    }
    if (testOnly && !testEmail.trim()) {
      toast.error("Vul een test-e-mailadres in");
      return;
    }
    if (!testOnly) {
      const ok = window.confirm(
        `Weet je zeker dat je deze nieuwsbrief naar ${subscribers} aanmelding(en) wil versturen?`
      );
      if (!ok) return;
    }
    setSending(true);
    try {
      const payload = {
        subject: form.subject.trim(),
        body: form.body,
        ...(testOnly ? { test_email: testEmail.trim() } : {}),
      };
      const res = await axios.post(`${API}/admin/campaigns`, payload, { headers: authHeaders });
      if (testOnly) {
        toast.success(`Test verstuurd naar ${res.data.test_sent_to}`);
      } else {
        toast.success(`Verzonden: ${res.data.sent} van ${res.data.total}`);
        setForm({ subject: "", body: "" });
        load();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Versturen mislukt";
      toast.error(typeof msg === "string" ? msg : "Versturen mislukt");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-campaigns-page" className="space-y-6">
      {/* Compose */}
      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
          <Send size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">
            NIEUWSBRIEF VERSTUREN
          </span>
          <span className="ml-auto text-xs text-[var(--tha-muted)]">
            <span className="text-white font-mono-x">{subscribers}</span> aanmeldingen
          </span>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              ONDERWERP
            </label>
            <input
              data-testid="campaign-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="block mt-1 w-full px-3 py-2 text-sm"
              placeholder="Bv. Tik Hem Aan! is live — proost mee!"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              BERICHT — DUBBELE ENTER VOOR NIEUWE ALINEA
            </label>
            <textarea
              data-testid="campaign-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={10}
              className="block mt-1 w-full px-3 py-2 text-sm resize-y"
              placeholder={"Hé bende,\n\nVandaag is het zover — onze eerste drop is officieel live..."}
            />
            <div className="mt-1 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              {form.body.length} TEKENS · ONZE BRAND-LAYOUT WORDT AUTOMATISCH TOEGEPAST
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-[var(--tha-border)]">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                TEST-E-MAIL (NIET OPGESLAGEN)
              </label>
              <input
                data-testid="campaign-test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="jij@email.nl"
                className="block mt-1 w-full px-3 py-2 text-sm"
              />
            </div>
            <button
              data-testid="campaign-test-button"
              type="button"
              onClick={() => send(true)}
              disabled={sending}
              className="btn-ghost-red px-4 py-2.5 inline-flex items-center gap-2 text-sm disabled:opacity-60"
            >
              <Eye size={14} /> TEST STUREN
            </button>
            <button
              data-testid="campaign-send-button"
              type="button"
              onClick={() => send(false)}
              disabled={sending || subscribers === 0}
              className="btn-red px-5 py-2.5 inline-flex items-center gap-2 font-display tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={14} /> {sending ? "VERSTUREN..." : `VERSTUUR NAAR ${subscribers}`}
            </button>
          </div>
        </div>
      </div>

      {/* Sent campaigns */}
      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
          <Mail size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">VERZONDEN</span>
          <span className="ml-auto text-xs text-[var(--tha-muted)]">{campaigns.length} totaal</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
        ) : campaigns.length === 0 ? (
          <div
            data-testid="campaigns-empty"
            className="p-10 text-center text-sm text-[var(--tha-muted)]"
          >
            Nog geen nieuwsbrieven verstuurd.
          </div>
        ) : (
          <ul>
            {campaigns.map((c) => {
              const isOpen = openCampaign === c.id;
              return (
                <li
                  key={c.id}
                  data-testid={`campaign-row-${c.id}`}
                  className="border-b border-[var(--tha-border)] last:border-0"
                >
                  <button
                    onClick={() => setOpenCampaign(isOpen ? null : c.id)}
                    className="w-full px-5 py-4 flex items-center gap-4 hover:bg-[#111] text-left"
                  >
                    <Clock
                      size={14}
                      className="text-[var(--tha-muted)] shrink-0"
                    />
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="text-sm text-white truncate">{c.subject}</div>
                      <div className="text-xs text-[var(--tha-muted)] truncate">
                        {new Date(c.created_at).toLocaleString("nl-NL")}
                      </div>
                      <div className="text-xs font-mono-x text-[var(--tha-muted)] text-right inline-flex items-center justify-end gap-2">
                        <CheckCircle size={12} className="text-[#22c55e]" />
                        {c.sent_count}/{c.recipients_count} BEZORGD
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 bg-[#070707]">
                      <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                        ONDERWERP
                      </div>
                      <div className="text-sm text-white mb-4">{c.subject}</div>
                      <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2">
                        INHOUD
                      </div>
                      <div className="border-l-2 border-[var(--tha-red)] bg-[#0a0a0a] p-4 whitespace-pre-wrap text-sm text-white">
                        {c.body}
                      </div>
                      {c.failed_count > 0 && (
                        <div className="mt-3 text-xs text-[var(--tha-red)] font-mono-x">
                          {c.failed_count} adres(sen) mislukt bij verzending
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminCampaigns;
